import React, { useCallback, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { Dropdown, Modal, Button, Badge, Image } from "react-bootstrap";
import { BsThreeDots } from "react-icons/bs";
import { PortfolioDashboard } from "../../../Common/Services/PortfolioDashboard";
import ChangeOwnerModal from "./ChangeOwner";
import { toast } from "react-toastify";
import { ReactPageLoader } from "../../../Common/Components/ReactPageLoader";
import { getSortedDashboardList } from "../Data/options";
import EllipsisWithTooltip from "react-ellipsis-with-tooltip";
import userIcon from "../../../Common/Assets/Images/profile.png";

const DashboardManageAll = ({
  viewAllModal,
  hasManagePortfolioPermission,
  handleSelection,
  handleCloseViewAll,
  handleOpenViewAll,
  handleDashboardDeleteConfirmation,
  registerFetchDashboards,
  handleShareDashboard,
  employees,
  GetDashboardMaster
}) => {
  const [dashboards, setDashboards] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const owners = useMemo(
    () => employees.filter((x) => x.type === 1),
    [employees]
  );

  const handleOpenModal = useCallback(() => setShowModal(true), []);
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedRow(null);
    fetchDashboards(false);
    GetDashboardMaster(false, null, false, true)
  }, []);

  const handleOpenChangeOwnerModal = useCallback(
    (row) => {
      setSelectedRow(row);
      handleOpenModal();
    },
    [handleOpenModal]
  );

  const handleSaveOwner = useCallback(
    (newOwnerId) => {
      const requestData: any = {
        DashboardName: selectedRow.dashboardName,
        Description: selectedRow.description,
        AllowDuplicate: 0,
      };

      requestData.DashboardId = selectedRow.dashboardId;
      requestData.DashboardLayout = selectedRow.dashboardLayout;
      requestData.CreatedById = newOwnerId;
      // requestData.DefaultDaterange = updatedItem.defaultDaterange;
      // requestData.DefaultProperties = updatedItem.defaultProperties;

      setIsSubmitting(true);
      PortfolioDashboard.AddEditDashboardMaster(requestData)
        .then((res) => {
          if (res.success) {
            let message = "Dashboard Owner changed successfully.";
            handleCloseModal();
            toast.success(message, {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          } else {
            toast.error("Something went wrong.", {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        })
        .catch((err) => {
          toast.error("Something went wrong.", {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [selectedRow]
  );

  const fetchDashboards = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await PortfolioDashboard.GetDashboardMasterViewAll();
      if (res.length > 0) {

        let result= res.map((item) => ({
          ...item,
          // id: item.dashboardId,
          name: item.dashboardName,
          owner: {
            name: item.createdByName,
            imagePath: item.userProfileImage,
            active: item.createdByStatus.toLowerCase() === "active",
          },
          totalCanViewUserCount: item?.totalCanViewUserCount || 0,
          totalCanViewRoleCount: item?.totalCanViewRoleCount || 0,
          totalCanWriteUserCount: item?.totalCanWriteUserCount || 0,
          totalCanWriteRoleCount: item?.totalCanWriteRoleCount || 0,
        }))

        result = getSortedDashboardList(result);
        setDashboards(result)
         
      } else {
        setDashboards([]);
      }
    } catch (error) {
      console.error("Failed to fetch dashboards:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Pass fetchDashboards to the parent
  useEffect(() => {
    if (registerFetchDashboards) {
      registerFetchDashboards(fetchDashboards);
    }
  }, []);

  // Fetch Dashboards
  useEffect(() => {
    if (viewAllModal) fetchDashboards();
  }, [viewAllModal]);

  const CanViewRender = useCallback(
    (row) => (
      <>
        <Badge className="mr-2">{row.totalCanViewUserCount} People</Badge>
        {row.totalCanViewRoleCount != 0 && (
          <Badge className="mr-2">{row.totalCanViewRoleCount} Role</Badge>
        )}
      </>
    ),
    []
  );

  const CanWriteRender = useCallback(
    (row) => (
      <>
        <Badge className="mr-2">{row.totalCanWriteUserCount} People</Badge>
        {row.totalCanWriteRoleCount != 0 && (
          <Badge className="mr-2">{row.totalCanWriteRoleCount} Role</Badge>
        )}
      </>
    ),
    []
  );
  // Table Columns
  const columns = useMemo(
    () => [
      {
        dataField: "name",
        text: "Dashboard",
        formatter: (_, row) => (
          <div
            onClick={() => {
              if(row?.canViewCanEdit){
                handleSelection(row);
                handleCloseViewAll();
              }
            }}
            style={{ cursor: row?.canViewCanEdit ? "pointer" : 'default'}}
          >
            <EllipsisWithTooltip placement="bottom">
            {row.name}
            </EllipsisWithTooltip>
          </div>
        ),
        
      },
      {
        dataField: "owner",
        text: "Owner",
        formatter: (cell, row) => (
          <div className="normal-text owner d-flex align-items-center">
            {!cell.imagePath ? (
              <div className="profile border rounded-circle" style={{ padding: "2px" }}>
                <img src={userIcon} width={22} height={22} alt="" />
              </div>
            ) : (
              <Image
                src={cell.imagePath}
                roundedCircle
                width={26}
                height={26}
                className="me-3"
              />
            )}
            <span style={{ maxWidth: "60%"}}>{row.orderBy == 1 ? "You"
             : 
             <EllipsisWithTooltip>{cell.name}</EllipsisWithTooltip>
             }</span>
            {!cell.active && <Badge className="ms-2">Inactive</Badge>}
          </div>
        ),
      },
      {
        dataField: "canView",
        text: "Can View",
        formatter: (_, row) => CanViewRender(row),
      },
      {
        dataField: "canEdit",
        text: "Can Edit",
        formatter: (_, row) => CanWriteRender(row),
      },
      {
        dataField: "actions",
        text: "",
        hidden: !hasManagePortfolioPermission,
        isDummyField: true,
        formatter: (_, row) => (
          <Dropdown className="share-dropdown">
            <Dropdown.Toggle
              id={`dropdown-${row.dashboardId}`}
              className="dropdown-toggle"
            >
              <BsThreeDots />
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu">
              <Dropdown.Item onClick={() => handleOpenChangeOwnerModal(row)}>
                Change Owner
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleShareDashboard(row.dashboardId)}
              >
                Share
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  handleDashboardDeleteConfirmation(row.dashboardId,true)
                }
                className="delete"
              >
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ),
      },
    ],
    [
      CanViewRender,
      CanWriteRender,
      handleOpenChangeOwnerModal,
      handleShareDashboard,
      handleDashboardDeleteConfirmation,
      hasManagePortfolioPermission
    ]
  );

  return (
    <div>
      {/* Main Modal for Dashboard List */}
      <Modal
        className="manage-dashboard-modal"
        show={viewAllModal}
        onHide={handleCloseViewAll}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage All Dashboards</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <ReactPageLoader useas={"manage-dashboard"} />
          ) : (
            <div className={hasManagePortfolioPermission === true ? "set-column-width" : "default-width"}>

              <BootstrapTable
                keyField="dashboardId"
                data={dashboards}
                columns={columns}
                bordered={false}
                hover
                noDataIndication={"No Dashboards found."}
              />
            </div>
            
          )}
        </Modal.Body>
      </Modal>
      {showModal && selectedRow && (
        <ChangeOwnerModal
          show={showModal}
          onClose={handleCloseModal}
          isSubmitting={isSubmitting}
          onSave={handleSaveOwner}
          owners={owners.filter(
            (item) => item?.userUniqueno != selectedRow?.createdByUniqueno
          )}
        />
      )}
    </div>
  );
};

export default React.memo(DashboardManageAll);
